import { canonicalizeType, kindOfType } from './taxonomy'
import type {
  LabeledBox,
  LabelerPrediction,
  LabelerTaxonomy,
  NormalizedBox,
} from './types'
import { predictionBbox } from './workingCopy'

/**
 * Compare-mode scoring: one extractor run against the working labels as
 * ground truth, matched greedily by IoU. Computed entirely in the browser so
 * the IoU slider recomputes instantly.
 *
 * Scoring discipline (each rule was a real failure mode):
 * - Ground truth defaults to VERIFIED boxes only — an untouched seed is the
 *   model's own output, and scoring against it reads ~100%.
 * - Untyped ground-truth boxes are excluded entirely: an unfinished box is
 *   missing work, not an assertion.
 * - A kind crossing is categorized separately from an ordinary type
 *   mismatch — it is the only misclassification that changes fused identity.
 */

export type ComparisonCategory =
  | 'true_positive'
  | 'text_mismatch'
  | 'type_mismatch'
  | 'kind_mismatch'
  | 'false_positive'
  | 'detection_miss'

export interface ComparisonItem {
  id: string
  category: ComparisonCategory
  bbox: NormalizedBox
  iou: number | null
  predictedText: string | null
  predictedType: string | null
  gtText: string | null
  gtType: string | null
  /** detection_miss only: the extractor saw nothing there vs saw something
   * it could not type. */
  missKind: 'not_detected' | 'unclassified' | null
}

export interface MetricsRow {
  precision: number
  recall: number
  f1: number
}

export interface ComparisonResult {
  items: ComparisonItem[]
  counts: {
    gt: number
    predicted: number
    matched: number
    missed: number
    falsePositives: number
    missedNotDetected: number
    missedUnclassified: number
  }
  metrics: {
    box: MetricsRow
    text: MetricsRow
    type: MetricsRow
    kind: MetricsRow
  }
}

export const COMPARISON_COLORS: Record<
  ComparisonCategory,
  { fill: string; stroke: string; dashed?: boolean; label: string }
> = {
  true_positive: {
    fill: 'rgba(34, 197, 94, 0.15)',
    stroke: 'rgba(34, 197, 94, 0.8)',
    label: 'Match',
  },
  text_mismatch: {
    fill: 'rgba(245, 158, 11, 0.15)',
    stroke: 'rgba(245, 158, 11, 0.8)',
    label: 'Text Mismatch',
  },
  type_mismatch: {
    fill: 'rgba(249, 115, 22, 0.15)',
    stroke: 'rgba(249, 115, 22, 0.8)',
    label: 'Type Mismatch',
  },
  kind_mismatch: {
    fill: 'rgba(220, 38, 38, 0.22)',
    stroke: 'rgba(220, 38, 38, 0.95)',
    label: 'Kind Crossing',
  },
  false_positive: {
    fill: 'rgba(168, 85, 247, 0.15)',
    stroke: 'rgba(168, 85, 247, 0.8)',
    label: 'False Positive',
  },
  detection_miss: {
    fill: 'rgba(239, 68, 68, 0.12)',
    stroke: 'rgba(239, 68, 68, 0.8)',
    dashed: true,
    label: 'Missed',
  },
}

export const COMPARISON_SEVERITY: ComparisonCategory[] = [
  'detection_miss',
  'kind_mismatch',
  'type_mismatch',
  'text_mismatch',
  'false_positive',
  'true_positive',
]

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase()

export function iou(a: NormalizedBox, b: NormalizedBox): number {
  const ix = Math.max(
    0,
    Math.min(a.x_max, b.x_max) - Math.max(a.x_min, b.x_min)
  )
  const iy = Math.max(
    0,
    Math.min(a.y_max, b.y_max) - Math.max(a.y_min, b.y_min)
  )
  const inter = ix * iy
  if (inter === 0) return 0
  const areaA = (a.x_max - a.x_min) * (a.y_max - a.y_min)
  const areaB = (b.x_max - b.x_min) * (b.y_max - b.y_min)
  return inter / (areaA + areaB - inter)
}

function metricsRow(correct: number, nPred: number, nGt: number): MetricsRow {
  const precision = nPred > 0 ? correct / nPred : 0
  const recall = nGt > 0 ? correct / nGt : 0
  const f1 =
    precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0
  return { precision, recall, f1 }
}

export function compareRunToLabels(args: {
  predictions: LabelerPrediction[]
  gtBoxes: LabeledBox[]
  taxonomy: LabelerTaxonomy | undefined
  iouThreshold: number
  verifiedOnly: boolean
}): ComparisonResult {
  const { predictions, taxonomy, iouThreshold, verifiedOnly } = args

  const gt = args.gtBoxes.filter(
    (b) => b.type !== null && (!verifiedOnly || b.verified)
  )

  const pairs: Array<{ p: number; g: number; iou: number }> = []
  predictions.forEach((pred, p) => {
    const pb = predictionBbox(pred)
    gt.forEach((box, g) => {
      const value = iou(pb, box.bbox)
      if (value >= iouThreshold) pairs.push({ p, g, iou: value })
    })
  })
  pairs.sort((a, b) => b.iou - a.iou)

  const predMatch = new Map<number, { g: number; iou: number }>()
  const gtMatch = new Set<number>()
  for (const pair of pairs) {
    if (predMatch.has(pair.p) || gtMatch.has(pair.g)) continue
    predMatch.set(pair.p, { g: pair.g, iou: pair.iou })
    gtMatch.add(pair.g)
  }

  const items: ComparisonItem[] = []
  let correctText = 0
  let correctType = 0
  let correctKind = 0

  predictions.forEach((pred, p) => {
    const predType = canonicalizeType(taxonomy, pred.type)
    const match = predMatch.get(p)
    if (!match) {
      items.push({
        id: `cmp-fp-${pred.id}`,
        category: 'false_positive',
        bbox: predictionBbox(pred),
        iou: null,
        predictedText: pred.text,
        predictedType: predType,
        gtText: null,
        gtType: null,
        missKind: null,
      })
      return
    }
    const box = gt[match.g]
    const textOk = norm(pred.text) === norm(box.text)
    const typeOk = predType === box.type
    const kindOk =
      kindOfType(taxonomy, predType) === kindOfType(taxonomy, box.type)
    if (textOk) correctText += 1
    if (typeOk) correctType += 1
    if (kindOk) correctKind += 1

    const category: ComparisonCategory = !kindOk
      ? 'kind_mismatch'
      : !typeOk
        ? 'type_mismatch'
        : !textOk
          ? 'text_mismatch'
          : 'true_positive'
    items.push({
      id: `cmp-m-${pred.id}`,
      category,
      bbox: box.bbox,
      iou: match.iou,
      predictedText: pred.text,
      predictedType: predType,
      gtText: box.text,
      gtType: box.type,
      missKind: null,
    })
  })

  let missedNotDetected = 0
  let missedUnclassified = 0
  gt.forEach((box, g) => {
    if (gtMatch.has(g)) return
    const overlappedByUntyped = predictions.some(
      (pred) =>
        canonicalizeType(taxonomy, pred.type) === null &&
        iou(predictionBbox(pred), box.bbox) >= iouThreshold
    )
    if (overlappedByUntyped) missedUnclassified += 1
    else missedNotDetected += 1
    items.push({
      id: `cmp-miss-${box.id}`,
      category: 'detection_miss',
      bbox: box.bbox,
      iou: null,
      predictedText: null,
      predictedType: null,
      gtText: box.text,
      gtType: box.type,
      missKind: overlappedByUntyped ? 'unclassified' : 'not_detected',
    })
  })

  const matched = predMatch.size
  const nPred = predictions.length
  const nGt = gt.length

  items.sort(
    (a, b) =>
      COMPARISON_SEVERITY.indexOf(a.category) -
      COMPARISON_SEVERITY.indexOf(b.category)
  )

  return {
    items,
    counts: {
      gt: nGt,
      predicted: nPred,
      matched,
      missed: nGt - matched,
      falsePositives: nPred - matched,
      missedNotDetected,
      missedUnclassified,
    },
    metrics: {
      box: metricsRow(matched, nPred, nGt),
      text: metricsRow(correctText, nPred, nGt),
      type: metricsRow(correctType, nPred, nGt),
      kind: metricsRow(correctKind, nPred, nGt),
    },
  }
}
